/**
 * ChangeEmailDialog — admin-only email change for investors/clients.
 *
 * UX flow:
 *   1. Admin opens dialog, types new email.
 *   2. Client-side check flags obviously invalid formats before hitting API.
 *   3. On submit:
 *      · 200 → success toast, `onSaved({ new_email, must_reset_password })` called.
 *      · 409 → shows the list of conflicting accounts + an "Override anyway"
 *              button. Clicking Override re-submits with `force_duplicate=true`
 *              — the backend audit-logs the override.
 *      · 400 → error toast with server's reason.
 *
 * Backend behaviour we surface here (must match backend contract):
 *   · Current password is invalidated → user must reset on next login.
 *   · A password-reset link is emailed to the NEW address.
 *   · A heads-up notification is emailed to both OLD and NEW addresses
 *     (when the old one looks like a real inbox).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { AlertTriangle, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

export default function ChangeEmailDialog({ open, onOpenChange, user, onSaved }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);  // { message, conflicts: [] }

  const reset = () => {
    setNewEmail('');
    setConflict(null);
    setSaving(false);
  };

  const doSubmit = async (force) => {
    const emailNorm = (newEmail || '').trim().toLowerCase();
    if (!EMAIL_RE.test(emailNorm)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (emailNorm === (user?.email || '').toLowerCase()) {
      toast.error('New email is the same as the current email');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/users/${user.id}/change-email`,
        { new_email: emailNorm, force_duplicate: !!force },
        { headers },
      );
      toast.success(r.data.message || 'Email updated');
      onSaved?.({
        new_email: r.data.new_email,
        must_reset_password: r.data.must_reset_password,
        notification_sent_to: r.data.notification_sent_to || [],
      });
      reset();
      onOpenChange?.(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === 'object' && detail.error === 'email_conflict') {
        setConflict(detail);   // show override UI
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to change email'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange?.(v); }}>
      <DialogContent className="max-w-lg" data-testid="change-email-dialog">
        <DialogHeader>
          <DialogTitle>Change login email</DialogTitle>
          <DialogDescription>
            Current: <span className="font-mono text-xs">{user.email}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!conflict && (
            <>
              <div>
                <Label>New email address</Label>
                <Input
                  type="email"
                  autoFocus
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="investor@example.com"
                  data-testid="change-email-input"
                />
              </div>

              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 flex gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <strong>Heads-up.</strong> Changing the email will:
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li>Force the user to <strong>reset their password</strong> on next login.</li>
                    <li>Email the NEW address a password-reset link (24-hour expiry).</li>
                    <li>Notify BOTH the old and new addresses of the change.</li>
                  </ul>
                </div>
              </div>
            </>
          )}

          {conflict && (
            <div className="space-y-3" data-testid="change-email-conflict">
              <div className="bg-red-50 border border-red-200 text-red-900 rounded p-3 flex gap-2 text-sm">
                <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold mb-1">Email already in use</div>
                  <div>{conflict.message}</div>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase text-gray-500">Conflicting accounts</Label>
                <div className="border rounded divide-y">
                  {conflict.conflicts.map((c) => (
                    <div key={c.id} className="p-2 text-sm flex items-center justify-between">
                      <div>
                        <div className="font-medium">
                          {c.first_name} {c.last_name}
                          {c.risk_participant_code && (
                            <Badge variant="outline" className="ml-2 text-xs">RP {c.risk_participant_code}</Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">{c.email}</div>
                      </div>
                      <Badge className="bg-slate-100 text-slate-700 text-xs">{c.role}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-xs text-gray-500">
                If this same person genuinely has multiple profiles (e.g. investor + client), click <strong>Override</strong> to allow the shared email. The override is logged in the audit trail.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>Cancel</Button>
          {conflict ? (
            <>
              <Button
                variant="outline"
                onClick={() => setConflict(null)}
                disabled={saving}
              >
                Change email
              </Button>
              <Button
                onClick={() => doSubmit(true)}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="change-email-override-btn"
              >
                {saving ? 'Saving…' : 'Override anyway'}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => doSubmit(false)}
              disabled={saving || !newEmail.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              data-testid="change-email-submit-btn"
            >
              {saving ? 'Saving…' : 'Change email'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
